import React, { useEffect, useRef, useState } from 'react';

interface BackgroundVisualizerProps {
  analyser: AnalyserNode | null;
  isPlaying: boolean;
}

const BackgroundVisualizer: React.FC<BackgroundVisualizerProps> = ({ analyser, isPlaying }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [fadeState, setFadeState] = useState<'in' | 'out' | 'hidden'>('hidden');

  // Handle the transition between playing and not playing states
  useEffect(() => {
    if (isPlaying) {
      setFadeState('in');
    } else {
      setFadeState('out');
      // After transition ends, set to hidden
      const timer = setTimeout(() => {
        setFadeState('hidden');
      }, 1500); // Return to original timing
      
      return () => clearTimeout(timer);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!analyser || !canvasRef.current) {
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    // Make canvas fullscreen
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Initialize canvas size and set up resize listener
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Synthwave color palette with more intense values
    const colors = {
      pink: 'rgba(255, 53, 184, 0.9)',        // Brighter pink
      dimPink: 'rgba(255, 53, 184, 0.4)',     // Less dim pink
      blue: 'rgba(10, 189, 255, 0.9)',        // Brighter blue
      dimBlue: 'rgba(10, 189, 255, 0.4)',     // Less dim blue
      purple: 'rgba(180, 20, 255, 0.8)',      // Brighter purple
      orange: 'rgba(255, 126, 10, 0.8)',      // Brighter orange
      darkPurple: 'rgba(40, 10, 80, 0.7)',    // Less dark purple
      brightYellow: 'rgba(255, 230, 0, 0.9)', // Bright yellow for accents
      brightGreen: 'rgba(0, 255, 140, 0.9)'   // Bright green for accents
    };
    
    // Time counter for animation effects
    let time = 0;
    
    // Sun/moon position and size
    const sunRadius = 40;
    
    // Calculate horizon position (needed for stars and other elements)
    const horizonY = canvas.height * 0.65;
    
    // State for vector particles (more particles)
    const particles = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: 1 + Math.random() * 4, // Larger particles
      speedX: (Math.random() * 4 - 2), // Faster movement
      speedY: (Math.random() * 4 - 2),
      color: Math.random() > 0.6 
        ? colors.pink 
        : Math.random() > 0.3 
          ? colors.blue 
          : colors.brightYellow
    }));
    
    // State for laser beams (new effect)
    const laserBeams = Array.from({ length: 8 }, () => ({
      angle: Math.random() * Math.PI * 2,
      width: 2 + Math.random() * 3,
      speed: 0.02 + Math.random() * 0.04,
      length: 0.5 + Math.random() * 0.5,
      active: false,
      color: Math.random() > 0.5 ? colors.pink : colors.blue
    }));
    
    // State for flash effect
    let flashIntensity = 0;
    let lastBeatTime = 0;
    let beatThreshold = 150;
    let prevAverage = 0;
    
    // Stars for background
    const stars = Array.from({ length: 70 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * horizonY * 0.8,
      size: Math.random() * 2,
      twinkle: Math.random()
    }));
    
    const draw = () => {
      // Only stop the animation if completely hidden
      if (fadeState === 'hidden') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        return;
      }

      animationRef.current = requestAnimationFrame(draw);
      time += 0.015; // Faster animation speed
      
      // Get frequency data
      analyser.getByteFrequencyData(dataArray);
      
      // Fade effect (less fade for more intensity)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate average for overall intensity
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Beat detection (for flash effects)
      if (average > prevAverage * 1.3 && average > beatThreshold && Date.now() - lastBeatTime > 250) {
        flashIntensity = 0.4;
        lastBeatTime = Date.now();
        
        // Activate random laser beam on beat
        const randomLaser = laserBeams[Math.floor(Math.random() * laserBeams.length)];
        randomLaser.active = true;
        randomLaser.angle = Math.random() * Math.PI * 2;
      }
      prevAverage = average;
      
      // Apply flash effect
      if (flashIntensity > 0) {
        ctx.fillStyle = `rgba(255, 255, 255, ${flashIntensity})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        flashIntensity -= 0.03; // Fade out speed
      }
      
      // Draw background stars
      for (const star of stars) {
        const twinkle = Math.max(0.1, 0.3 + Math.sin(time * 3 + star.twinkle * 10) * 0.7);
        ctx.fillStyle = `rgba(255, 255, 255, ${twinkle * 0.8})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * twinkle, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // 1. Draw the synthwave sun/moon with pulse effect
      const bassAvg = dataArray.slice(0, 10).reduce((a, b) => a + b, 0) / 10;
      const bassIntensity = bassAvg / 255;
      const size = sunRadius + (bassAvg / 255) * 50; // More dramatic size changes
      const sunY = horizonY - size * 2.5; // Increased from 1.5 to 2.5 to move sun much higher
      
      // Create pulsating sun with more dramatic glow 
      // Create stronger gradient for the glow
      const glow = ctx.createRadialGradient(
        centerX, sunY, size * 0.5,
        centerX, sunY, size * 3 // Larger glow radius
      );
      glow.addColorStop(0, colors.orange);
      glow.addColorStop(0.3, `rgba(255, 100, 20, ${0.3 + bassIntensity * 0.5})`);
      glow.addColorStop(1, 'rgba(255, 100, 20, 0)');
      
      // Draw the glow
      ctx.beginPath();
      ctx.arc(centerX, sunY, size * 3, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();
      
      // Draw the sun/moon itself with pulsating effect
      ctx.beginPath();
      ctx.arc(centerX, sunY, size, 0, Math.PI * 2);
      
      // Create gradient for sun/moon
      const gradient = ctx.createLinearGradient(
        centerX, sunY - size, 
        centerX, sunY + size
      );
      gradient.addColorStop(0, colors.orange);
      gradient.addColorStop(1, colors.pink);
      
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Add dynamic sun rays on strong bass
      if (bassAvg > 80) {
        const rayCount = 8;
        const rayLength = size * (1 + bassIntensity * 1.5);
        
        ctx.strokeStyle = `rgba(255, 100, 20, ${bassIntensity * 0.7})`;
        ctx.lineWidth = 3 + bassIntensity * 5;
        
        for (let i = 0; i < rayCount; i++) {
          const angle = (i / rayCount) * Math.PI * 2 + time * 0.2; // Slowly rotating rays
          const rayX = centerX + Math.cos(angle) * rayLength;
          const rayY = sunY + Math.sin(angle) * rayLength;
          
          ctx.beginPath();
          ctx.moveTo(centerX, sunY);
          ctx.lineTo(rayX, rayY);
          ctx.stroke();
        }
      }
      
      // Draw horizontal lines for the "horizon" with pulsating effect
      for (let i = 0; i < 5; i++) { // More lines
        const y = horizonY + i * 3;
        const pulseOffset = Math.sin(time * 3 + i) * 5 * bassIntensity; // Adding pulsation
        
        ctx.beginPath();
        ctx.moveTo(0, y + pulseOffset);
        ctx.lineTo(canvas.width, y + pulseOffset);
        ctx.strokeStyle = i === 0 
          ? `rgba(255, 53, 184, ${0.7 + bassIntensity * 0.3})` 
          : `rgba(255, 53, 184, ${0.4 - i * 0.07})`;
        ctx.lineWidth = 2 - i * 0.3;
        ctx.stroke();
      }
      
      // 2. Draw perspective grid (classic synthwave grid) with more dynamic effects
      const gridCount = 16; // More grid lines
      const gridSpacing = canvas.width / gridCount;
      
      // Vertical grid lines with stronger glow
      ctx.shadowBlur = 15 + bassIntensity * 10;
      ctx.shadowColor = colors.pink;
      
      for (let i = 0; i <= gridCount; i++) {
        // Create more dramatic oscillation effect
        const offset = Math.sin(time * 3 + i * 0.3) * 8 * (1 + bassIntensity);
        const x = i * gridSpacing + offset;
        
        // Skip middle lines for cleaner look
        if (i > gridCount/2 - 3 && i < gridCount/2 + 3) continue;
        
        ctx.beginPath();
        ctx.moveTo(x, horizonY);
        ctx.lineTo(centerX + (x - centerX) * 3, canvas.height + 150); // Extend further
        
        // More dynamic colors based on music
        const intensityShift = Math.sin(time * 2 + i * 0.5) * 0.3 + 0.7;
        ctx.strokeStyle = i % 2 === 0 
          ? `rgba(10, 189, 255, ${intensityShift * (0.6 + bassIntensity * 0.4)})` 
          : `rgba(10, 189, 255, ${intensityShift * (0.4 + bassIntensity * 0.3)})`;
        ctx.lineWidth = 2 + bassIntensity * 2;
        ctx.stroke();
      }
      
      // Reset shadow
      ctx.shadowBlur = 0;
      
      // 3. Horizontal grid lines with more dynamic spacing
      if (average > 20) {
        const horizontalLines = 15; // More lines
        const lineSpacing = (canvas.height - horizonY) / horizontalLines;
        
        for (let i = 1; i <= horizontalLines; i++) {
          // Add wave effect to horizontal lines
          const waveAmp = 5 * bassIntensity;
          const waveFreq = i * 0.2;
          
          ctx.beginPath();
          
          // Add wave pattern to lines
          for (let x = 0; x < canvas.width; x += 20) {
            const waveY = Math.sin(time * 2 + x * 0.01 + i * 0.5) * waveAmp * (i / horizontalLines);
            const y = horizonY + i * lineSpacing + waveY;
            
            if (x === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          
          const alpha = 0.8 - (i / horizontalLines) * 0.6; // Fade with distance
          ctx.strokeStyle = `rgba(10, 189, 255, ${alpha + bassIntensity * 0.2})`;
          ctx.lineWidth = 1 + Math.max(0, 1 - i / 5);
          ctx.stroke();
        }
      }
      
      // 4. More dynamic triangular mountains on the horizon
      ctx.beginPath();
      
      // Create jagged mountain silhouettes with movement
      const mountains = 10; // More mountains
      const mountainWidth = canvas.width / mountains;
      
      for (let i = 0; i <= mountains; i++) {
        const x = i * mountainWidth;
        // More dramatic height changes
        const mountainHeight = 70 + Math.sin(i * 2.5 + time) * 40;
        
        if (i === 0) {
          ctx.moveTo(x, horizonY);
        } else {
          const peakX = x - mountainWidth/2;
          ctx.lineTo(peakX, horizonY - mountainHeight);
          ctx.lineTo(x, horizonY);
        }
      }
      
      // Create gradient for mountains with more intense colors
      const mountainGradient = ctx.createLinearGradient(0, horizonY - 100, 0, horizonY);
      mountainGradient.addColorStop(0, `rgba(180, 20, 255, ${0.7 + bassIntensity * 0.3})`);
      mountainGradient.addColorStop(1, colors.darkPurple);
      
      ctx.fillStyle = mountainGradient;
      ctx.fill();
      
      // 5. Add glowing peaks to mountains when music is intense
      if (average > 80) {
        ctx.shadowBlur = 20;
        ctx.shadowColor = colors.pink;
        ctx.strokeStyle = colors.pink;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
      
      // 6. Sound-reactive neon particles (more and faster)
      const highFreqAvg = dataArray.slice(bufferLength / 2, bufferLength * 0.75).reduce((a, b) => a + b, 0) / (bufferLength * 0.25);
      const highFreqIntensity = highFreqAvg / 255;
      if (highFreqAvg > 30) { // Lower threshold to show more often
        const particlesToShow = Math.floor(20 + highFreqAvg / 10); // Show more particles
        
        // Add glow effect to particles
        ctx.shadowBlur = 10 + highFreqIntensity * 15;
        
        // Update and draw particles
        for (let i = 0; i < Math.min(particles.length, particlesToShow); i++) {
          const p = particles[i];
          
          // Update position with more dramatic speed
          p.x += p.speedX * (highFreqAvg / 70 + 0.5);
          p.y += p.speedY * (highFreqAvg / 70 + 0.5);
          
          // Wrap around screen edges
          if (p.x < 0) p.x = canvas.width;
          if (p.x > canvas.width) p.x = 0;
          if (p.y < 0) p.y = canvas.height;
          if (p.y > canvas.height) p.y = 0;
          
          // Draw neon point with size variation
          const sizeMultiplier = 1 + Math.sin(time * 3 + i) * 0.5;
          ctx.shadowColor = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * sizeMultiplier, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
          
          // Add particle trails on high intensity
          if (highFreqAvg > 100 && i % 3 === 0) {
            const trailLength = 5 + highFreqIntensity * 15;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x - p.speedX * trailLength, p.y - p.speedY * trailLength);
            ctx.strokeStyle = p.color;
            ctx.lineWidth = p.size / 2;
            ctx.stroke();
          }
        }
        
        // Reset shadow
        ctx.shadowBlur = 0;
      }
      
      // 7. Laser beam effects that fire on strong beats
      ctx.shadowBlur = 15;
      for (const laser of laserBeams) {
        if (!laser.active) continue;
        
        // Update laser angle
        laser.angle += laser.speed;
        
        // Draw laser
        const length = canvas.width * laser.length;
        const endX = centerX + Math.cos(laser.angle) * length;
        const endY = sunY + Math.sin(laser.angle) * length;
        
        ctx.shadowColor = laser.color;
        ctx.strokeStyle = laser.color;
        ctx.lineWidth = laser.width;
        ctx.beginPath();
        ctx.moveTo(centerX, sunY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        
        // Randomly deactivate lasers
        if (Math.random() < 0.02) {
          laser.active = false;
        }
      }
      ctx.shadowBlur = 0;
      
      // 8. Draw retro-style equalizer with neon glow and more blocks
      if (average > 20) { // Lower threshold to show more often
        const barCount = 16; // More bars
        const barWidth = Math.ceil(canvas.width / barCount);
        const barSpacing = 6;
        const blockSize = 8;
        const maxBlocks = 15; // More blocks for taller visualization
        
        // Add glow effect to equalizer
        ctx.shadowBlur = 15 + bassIntensity * 10;
        ctx.shadowColor = colors.blue;
        
        for (let i = 0; i < barCount; i++) {
          // Sample from across the frequency spectrum
          const index = Math.floor((i / barCount) * (bufferLength / 2.5));
          const value = dataArray[index];
          const blocks = Math.floor((value / 255) * maxBlocks);
          
          // Make blocks pulsate
          const pulseOffset = Math.sin(time * 3 + i * 0.4) * 2;
          
          // Draw blocks
          for (let j = 0; j < blocks; j++) {
            const x = i * barWidth + barSpacing + pulseOffset;
            const y = canvas.height - (j + 1) * blockSize - 30;
            
            // More dynamic color gradient based on height and music
            const colorRatio = j / maxBlocks;
            let barColor;
            
            if (colorRatio < 0.25) {
              barColor = colors.blue;
            } else if (colorRatio < 0.5) {
              barColor = colors.purple;
            } else if (colorRatio < 0.75) {
              barColor = colors.pink;
            } else {
              barColor = colors.brightYellow; // Extra color for highest blocks
            }
            
            // Add extra brightness on beats
            const extraBrightness = flashIntensity > 0 ? 1.3 : 1;
            
            ctx.shadowColor = barColor;
            ctx.fillStyle = barColor;
            ctx.fillRect(
              x, 
              y, 
              barWidth - barSpacing * 2 + Math.sin(time * 5 + j * 0.2) * 2, // Width varies
              blockSize - 1
            );
          }
        }
        
        // Reset shadow
        ctx.shadowBlur = 0;
      }
      
      // 9. Add occasional bright flashes across the grid on strong beats
      const midFreqAvg = dataArray.slice(bufferLength / 4, bufferLength / 2).reduce((a, b) => a + b, 0) / (bufferLength / 4);
      if (midFreqAvg > 120 && Math.random() < 0.1) {
        // Create diagonal light beam
        const startX = Math.random() * canvas.width;
        const endX = Math.random() * canvas.width;
        
        ctx.beginPath();
        ctx.moveTo(startX, horizonY);
        ctx.lineTo(endX, 0);
        
        // Add glow
        ctx.shadowBlur = 30;
        ctx.shadowColor = colors.brightYellow;
        ctx.strokeStyle = colors.brightYellow;
        ctx.lineWidth = 2 + bassIntensity * 6;
        ctx.stroke();
        
        // Reset shadow
        ctx.shadowBlur = 0;
      }
      
      // 10. Draw glitches on very strong frequencies
      if (average > 100 && Math.random() < 0.2) {
        const glitchCount = Math.floor(3 + Math.random() * 4);
        
        for (let i = 0; i < glitchCount; i++) {
          const x = Math.random() * canvas.width;
          const y = Math.random() * canvas.height;
          const width = 20 + Math.random() * 100;
          const height = 5 + Math.random() * 15;
          
          ctx.fillStyle = i % 2 === 0 ? colors.pink : colors.blue;
          ctx.fillRect(x, y, width, height);
        }
      }
    };

    // Start animation if in fade-in or fade-out state
    if (fadeState === 'in' || fadeState === 'out') {
      draw();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [analyser, fadeState]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ 
        mixBlendMode: 'screen', 
        opacity: fadeState === 'in' ? 0.7 : fadeState === 'out' ? 0 : 0,
        transform: fadeState === 'in' ? 'scale(1)' : 'scale(0.97)',
        filter: fadeState === 'in' ? 'blur(0px)' : 'blur(2px)',
        transition: 'opacity 1.2s ease-out, transform 1.2s ease-out, filter 1.2s ease-out',
        zIndex: 15,
        visibility: 'visible',
      }}
    />
  );
};

export default React.memo(BackgroundVisualizer); 